// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type Concentration } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { type PointTooltip, ResponsiveLine } from '@nivo/line';
import { type FC, useCallback, useMemo } from 'react';
import { ChartContainer } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';

export interface ConcentrationGraphProps {
  concentrations: Concentration[];
}

export const ConcentrationGraph: FC<ConcentrationGraphProps> = ({
  concentrations,
}) => {
  const { t } = useTranslation('common');
  const data = useMemo(
    () => [
      {
        id: 'Concentration',
        data: [{ x: 0, y: '0' }].concat(
          concentrations.map((concentration, index) => ({
            x: index + 1,
            y: (concentration.concentration * 100).toFixed(2),
          })),
        ),
      },
    ],
    [concentrations],
  );

  const maxConcentration = useMemo(
    () => Math.max(...concentrations.map(c => c.concentration * 100)),
    [concentrations],
  );

  const tooltip: PointTooltip = useCallback(
    ({ point }) => {
      return (
        <div className="bg-white p-1">
          <div>
            <strong>{t('common.stocks')}:</strong> {point.data.x as string}
          </div>
          <div>
            <strong>{t('common.weight')}:</strong> {point.data.yFormatted}
          </div>
        </div>
      );
    },
    [t],
  );

  return (
    <ChartContainer
      header={t('common.concentration')}
      popupName="charts.concentration"
      tooltip
    >
      <div className="h-[420px]">
        <ResponsiveLine
          data={data}
          margin={{ top: 20, right: 20, bottom: 50, left: 70 }}
          xScale={{
            type: 'linear',
            min: 0,
            max: concentrations.length,
          }}
          yScale={{
            type: 'linear',
            min: 0,
            max: maxConcentration,
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            legend: t('common.numberOfStocks'),
            legendOffset: 44,
            legendPosition: 'middle',
            tickValues: 5,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickValues: 10,
            legend: t('common.totalPortfolioPercentage'),
            legendOffset: -65,
            legendPosition: 'middle',
          }}
          theme={{
            axis: {
              legend: {
                text: {
                  fontSize: 12,
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
              ticks: {
                text: {
                  fontSize: 12,
                  fontFamily: 'poppins',
                  fill: 'var(--blueGrayMuted)',
                },
              },
            },
          }}
          enableGridX={false}
          enableGridY={true}
          colors={'rgba(16, 114, 186, 1.0)'}
          enablePoints={false}
          enableCrosshair={true}
          crosshairType="bottom"
          useMesh={true}
          tooltip={tooltip}
          animate={false}
        />
      </div>
    </ChartContainer>
  );
};
